import { AxiosHeaders, isAxiosError } from "axios";
/**
 * Given a value that was thrown (in javascript all sorts of values can be
 * thrown, not only Error-s), tries to extract as much information from it as
 * possible.
 */
export function extractThrownValueDetails(thrownValue) {
    try {
        // Primitive value
        if (typeof thrownValue === "string" ||
            typeof thrownValue === "number" ||
            typeof thrownValue === "boolean" ||
            thrownValue === null ||
            typeof thrownValue === "undefined" ||
            typeof thrownValue === "symbol" ||
            typeof thrownValue === "bigint") {
            return {
                kind: "primitive",
                message: "primitive value thrown",
                thrownValueType: typeof thrownValue,
                stringifiedThrownValue: String(thrownValue),
            };
        }
        // Function value
        if (typeof thrownValue === "function") {
            return {
                kind: "function",
                message: "function value thrown",
                thrownFunctionName: thrownValue.name,
            };
        }
        // Object value
        const obj = thrownValue;
        return {
            kind: "object",
            message: obj["message"],
            name: obj["name"],
            stack: obj["stack"],
            code: obj["code"],
            signal: obj["signal"],
            ...(isAxiosError(thrownValue)
                ? extractAxiosErrorDetails(thrownValue)
                : { content: safeStringify(obj) }),
        };
    }
    catch {
        return {
            kind: "unextractable",
            message: "a value was thrown for which it was not possible to extract any detail",
        };
    }
}
export function describeThrownValueDetails(details) {
    if (!details || typeof details !== "object" || !("kind" in details)) {
        return describeThrownValueDetails(extractThrownValueDetails(details));
    }
    switch (details.kind) {
        case "primitive":
            return details.stringifiedThrownValue;
        case "function":
            return details.thrownFunctionName
                ? `function ${details.thrownFunctionName}`
                : details.message;
        case "object":
            if (typeof details.message === "string" && details.message)
                return details.message;
            if (typeof details.name === "string" && details.name)
                return details.name;
            if (typeof details.content === "string")
                return details.content.slice(0, 500);
            try {
                return JSON.stringify(details).slice(0, 500);
            }
            catch {
                return "non-serializable error object";
            }
        case "unextractable":
            return details.message;
        default:
            // Exhaustive per the type, but `details` can still be malformed at
            // runtime (see above) — describe it rather than crash.
            try {
                return JSON.stringify(details).slice(0, 500);
            }
            catch {
                return "non-serializable error object";
            }
    }
}
function safeStringify(value) {
    try {
        return JSON.stringify(value) ?? String(value);
    }
    catch {
        return "non-serializable object";
    }
}
function extractAxiosErrorDetails(error) {
    return {
        axiosConfig: error.config
            ? {
                method: error.config.method,
                baseURL: error.config.baseURL,
                url: error.config.url,
                params: error.config.params,
                headers: redactRequestHeaders(AxiosHeaders.prototype.toJSON.call(error.config.headers)),
                data: error.config.data,
            }
            : undefined,
        axiosResponse: error.response
            ? {
                status: error.response.status,
                headers: AxiosHeaders.prototype.toJSON.call(error.response.headers),
                data: error.response.data,
            }
            : undefined,
    };
}
function redactRequestHeaders(headers) {
    const headersToRedact = new Set(["authorization"]);
    Object.keys(headers).forEach((header) => {
        if (headersToRedact.has(header.toLowerCase())) {
            headers[header] = "REDACTED";
        }
    });
    return headers;
}
//# sourceMappingURL=extractThrownValueDetails.js.map